// the library is a chunk (PhotoSwipeLoader.js); its styles come with the stylesheet of each
// bundle (eventgallery.js, eventgallery-backend.js)
import {loadPhotoSwipe, photoSwipeLibrary} from "./PhotoSwipeLoader";
import PhotoSwipeVideoPlugin from '../photoswipe-video-plugin/index';
import {Helpers} from "../../../common/js/Helpers";
import {icon} from "../../../common/js/icon";
import {prefersReducedMotion} from "../../../common/js/motion";
import Polyfill from "../Polyfill";
import Overlay from "../Overlay";
import SlideShow from "../SlideShow";
import {parseHash} from "./UrlHandling";
import {closest, getThumbnailContainers, parseThumbnailElement} from "./SlideGenerator";
import {Events} from "../minicart/CartEvents";
import HistoryHandler from "./HistoryHandler";
import LightboxModality from "./LightboxModality";
import {lightboxPadding, safeAreaOf} from "./LightboxPadding";
import {bindImageFallback} from "./LightboxImageFallback";



export default class PhotoSwipeGallery {

    constructor(options) {
        this._isOpen = false;
        this._gallery = null;
        this._videoPlugin = null;
        // the last openPhotoSwipe(): an earlier one which waits for the library opens nothing
        this._request = 0;
        this._waiting = false;
        // the listeners setAfterChangeEventListener() got while the library was on its way
        this._changeListeners = [];
        this.options = Helpers.mergeObjects({
            doOverlapToolbar: false,
            imagePadding: 0,
            doOverlapImageWithCaption: false,
            showHideAnimationType: 'zoom',
        }, options);
    }

    initPhotoSwipe() {
        this.translations = this._translate();

        // Parse URL and open gallery if it contains #&pid=3&gid=1
        // (the library is loaded at once for it)
        let hashData = parseHash();
        if(hashData.pid && hashData.gid) {
            this.openPhotoSwipe( hashData.pid ,  this.getGalleries().get(hashData.gid), true, true )
                .catch((error) => console.error('The lightbox could not load.', error));
        }
    }

    _translate() {
        if (!window.EventGalleryLightboxConfiguration) {
            return {};
        }

        return {
            closeTitle: window.EventGalleryLightboxConfiguration.KEY_CLOSE,
            zoomTitle: window.EventGalleryLightboxConfiguration.KEY_ZOOM,
            arrowPrevTitle: window.EventGalleryLightboxConfiguration.KEY_PREVIOUS,
            arrowNextTitle: window.EventGalleryLightboxConfiguration.KEY_NEXT,
            errorMsg: window.EventGalleryLightboxConfiguration.KEY_IMAGE_LOAD_FAILED || 'The image could not be loaded',
            indexIndicatorSep: ' / ',
            share: window.EventGalleryLightboxConfiguration.KEY_SHARE,
            download: window.EventGalleryLightboxConfiguration.KEY_DOWNLOAD,
            buy: window.EventGalleryLightboxConfiguration.KEY_BUY,
            buyVideo: window.EventGalleryLightboxConfiguration.KEY_BUY_VIDEO,
            'playpause-buttonTitle': window.EventGalleryLightboxConfiguration.KEY_PLAYSLIDESHOW,
            pause: window.EventGalleryLightboxConfiguration.KEY_PAUSESLIDESHOW,
            // the name of the lightbox as a dialog (LightboxModality.js)
            lightboxLabel: window.EventGalleryLightboxConfiguration.KEY_LIGHTBOX_LABEL,
        }
    }

    /**
     * A click of the document: a click on a thumbnail of a gallery (a[data-eg-lightbox]) opens
     * the lightbox. EventgalleryBehavior.js binds it once, before the body is parsed, so a
     * thumbnail opens the lightbox as soon as it is on the screen, and a thumbnail a script adds
     * later (the cart, the mini cart, the ajax paging) needs no binding of its own. Until the
     * review of #1894 every thumbnail got its click at DOMContentLoaded, which deferred scripts
     * of the page can hold back by seconds on a slow phone, and a tap before it opened the file
     * of the image instead of the lightbox.
     *
     * @param {MouseEvent} e
     */
    onDocumentClick(e) {
        const anchor = closest(e.target, (el) => el.tagName && el.tagName.toUpperCase() === 'A');
        if (anchor && anchor.hasAttribute('data-eg-lightbox') && this.getSlideBy(anchor) !== null) {
            this.onThumbnailsClick(e);
        }
    }

    onThumbnailsClick(e) {
        e = e || window.event;
        e.preventDefault ? e.preventDefault() : e.returnValue = false;

        let eTarget = e.target || e.srcElement;

        // leave the icons for cart&sharing alone
        let iconContainer = closest(eTarget, function(el) {
            return (el.classList && el.classList.contains('eventgallery-icon-container'));
        });
        if (iconContainer !== null) {
            return;
        }

        // find root element of slide
        let clickedListItem = closest(eTarget, function(el) {
            return (el.tagName && el.tagName.toUpperCase() === 'A');
        });

        if(!clickedListItem) {
            return;
        }

        let slide = this.getSlideBy(clickedListItem);
        if (slide === null) {
            return false;
        }
        let index = this.getIndexOfSlide(slide);
        let slides = this.getGalleries().get(slide.gid);

        if(index >= 0) {
            // the lightbox zooms from the thumbnail which was clicked - one of several of the
            // same image - and gives the focus back to it
            slides[index].thumbEl = clickedListItem;
            // open PhotoSwipe if valid index found; without its library the link does what
            // it does without a script: it shows the file of the image
            this.openPhotoSwipe( index, slides ).catch((error) => {
                console.error('The lightbox could not load.', error);
                this._followLink(clickedListItem);
            });
        }
        return false;
    }

    /**
     * @param {HTMLAnchorElement} anchor
     */
    _followLink(anchor) {
        window.location.assign(anchor.href);
    }

    /**
     * Opens the lightbox: at once when the library of PhotoSwipe is in, else as soon as its chunk
     * has arrived (PhotoSwipeLoader.js). A later call while the chunk is on its way wins: only
     * the last one opens.
     *
     * @param index
     * @param slides
     * @param disableAnimation
     * @param fromURL
     * @param useHistory
     * @return {Promise<boolean>} whether the lightbox opened; rejected when the chunk did not load
     */
    openPhotoSwipe(index, slides, disableAnimation, fromURL, useHistory = true) {
        if (slides === undefined || slides.length === 0 ) {
            return Promise.resolve(false);
        }
        const request = ++this._request;
        const open = (PhotoSwipe) => {
            if (request !== this._request) {
                return false;
            }
            this._waiting = false;
            return this._open(PhotoSwipe, index, slides, disableAnimation, fromURL, useHistory);
        };

        const library = photoSwipeLibrary();
        if (library) {
            this._waiting = false;
            return Promise.resolve(open(library));
        }
        this._waiting = true;
        return loadPhotoSwipe().then(open, (error) => {
            if (request === this._request) {
                this._waiting = false;
                this._changeListeners = [];
            }
            throw error;
        });
    }

    /**
     * @param {Function} PhotoSwipe the class of the library
     * @return {boolean} whether the lightbox opened
     */
    _open(PhotoSwipe, index, slides, disableAnimation, fromURL, useHistory) {
        // a tap before DOMContentLoaded opens the lightbox before initPhotoSwipe() has run
        if (!this.translations) {
            this.translations = this._translate();
        }

        // define options (if needed)
        let options = Helpers.mergeObjects({
            pswpModule: PhotoSwipe,

            galleryPIDs:true,
            bgOpacity: 1,
            trapFocus: false,
            showHideAnimationType: this.options.showHideAnimationType,

            // define gallery index (for URL)
            galleryUID: slides[0].gid,

            'tapAction': () => {
                this._gallery.element.classList.toggle('pswp--ui-visible');
                if (this._gallery.element.classList.contains('pswp--ui-visible')) {
                    this._gallery.element.classList.remove('pswp--ui-invisible');
                } else {
                    this._gallery.element.classList.add('pswp--ui-invisible');
                }

                this._gallery.updateSize(true);
            },

            // the room around the image: the bars and the safe areas (LightboxPadding.js)
            paddingFn: (viewportSize, itemData) => this._padding(itemData),
        }, this.translations);

        if (window.EventGalleryLightboxConfiguration) {
            if (parseInt(window.EventGalleryLightboxConfiguration.navigationFadeDelay) > 0) {
                options.timeToIdle = parseInt(window.EventGalleryLightboxConfiguration.navigationFadeDelay);
                options.timeToIdleOutside = parseInt(window.EventGalleryLightboxConfiguration.navigationFadeDelay);
            }
        }

        // PhotoSwipe opened from URL
        if(fromURL) {
            if(options.galleryPIDs) {
                index = parseInt(index, 10);
                // parse real index when custom PIDs are used
                // http://photoswipe.com/documentation/faq.html#custom-pid-in-url
                for(let j = 0; j < slides.length; j++) {
                    if(slides[j].pid === index) {
                        options.index = j;
                        break;
                    }
                }
            } else {
                // in URL indexes start from 1
                options.index = parseInt(index, 10) - 1;
            }
        } else {
            options.index = parseInt(index, 10);
        }

        // exit if index not found
        if( isNaN(options.index) ) {
            this._changeListeners = [];
            return false;
        }

        if(disableAnimation) {
            options.showAnimationDuration = 0;
        }

        // Pass data to PhotoSwipe and initialize it

        options.dataSource = slides;


        this._gallery = new PhotoSwipe(options);
        for (const listener of this._changeListeners.splice(0)) {
            this._gallery.on('change', listener);
        }

        // a modal dialog with a name which takes the focus and gives it back to what opened
        // it: the focused control, else the thumbnail of a click (Safari does not focus a
        // clicked link); a lightbox which opened from the address of the page has no opener
        const focused = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
        LightboxModality.bind(this._gallery, {
            label: this.translations.lightboxLabel || '',
            opener: fromURL ? null : (focused || slides[options.index]?.thumbEl || null),
        });


        /** allows the zoom closing */
         
        this._gallery.addFilter('thumbEl', (thumbEl, data, index) => {
            if (data.thumbEl) {
                const el = data.thumbEl.querySelector('img');
                if (el) {
                    return el;
                }
            }
            return thumbEl;
        });

        /** get a placeholder */
        /*this._gallery.addFilter('placeholderSrc', (placeholderSrc, slide) => {
            const el = slide.data.thumbEl.querySelector('img');
            if (el) {
                return el.src;
            }
            return placeholderSrc;
        });*/

        this._gallery.on('close', () => {
            return this._isOpen = false;
        });

        this._gallery.on('change', () => {
            this.overlay?.closeOverlay();
        })

        // HISTORY

        let historyHandler = new HistoryHandler({
            onClose: () => this._gallery.close(),
            history: useHistory ? history : null
        })

        this._gallery.on('afterInit', () => {
            historyHandler.startUrlHistory();
            historyHandler.adjustUrlHistory(this.getCurrentSlide());
        });

        this._gallery.on('change', () => {
            historyHandler.adjustUrlHistory(this.getCurrentSlide());
        })

        this._gallery.on('close', () => {
            historyHandler.endUrlHistory();
        })
        // END HISTORY

        this._gallery.on('keydown', (e) => {
            // prevent photoswipe to react on keypresses
            if (this.overlay?.isOpen) {
                e.defaultPrevented = true;
            }
        })

        this._gallery.on('close', () => {
            this.overlay?.closeOverlay();
        })

        this._gallery.on('change', () => {
            let data = {
                "id": this.getCurrentSlide().pid,
            }
            document.dispatchEvent(Polyfill.createNewEvent('eventgallery-lightbox-changed', data));
        });

        this._registerUIElements();

        // a large file which does not load falls back to the next smaller one of the srcset
        bindImageFallback(this._gallery);

        this._videoPlugin = new PhotoSwipeVideoPlugin(this._gallery, {
            // a video plays as soon as its slide opens, unless the visitor asked for less motion:
            // then the play button of the player starts it (A11Y-12)
            autoplay: !prefersReducedMotion(),
        });

        this._gallery.init();

        this._isOpen = true;

        let data = {
            id: this.getCurrentSlide().pid
        }
        document.dispatchEvent(Polyfill.createNewEvent('eventgallery-lightbox-opened', data));

        return true;
    }

    /**
     * The padding of PhotoSwipe around an image: what the bars take, measured on the page -
     * the top bar and a copy of the caption of the image -, and the safe areas of the screen.
     *
     * @param itemData the slide
     * @returns {{top: number, bottom: number, left: number, right: number}}
     */
    _padding(itemData) {
        const root = this._gallery.element;
        const barsVisible = !root.classList.contains('pswp--ui-invisible');
        const lowScreen = window.matchMedia('(max-height: 520px)').matches;
        let topBarHeight = 0;
        let captionHeight = 0;

        if (barsVisible && !lowScreen) {
            topBarHeight = Helpers.getElementHeight(root.querySelector('.pswp__top-bar'));
            if (!this.options.doOverlapImageWithCaption && itemData.title) {
                const el = document.createElement('div');
                el.className = 'pswp__custom-caption';
                el.innerHTML = itemData.title;
                root.appendChild(el);
                captionHeight = Helpers.getElementHeight(el);
                const captionBottom = parseInt(getComputedStyle(el).bottom, 10);
                if (!isNaN(captionBottom)) {
                    captionHeight += captionBottom + Math.max(0, 4 - this.options.imagePadding);
                }
                Polyfill.removeHtmlElementNode(el);
            }
        }

        return lightboxPadding({
            barsVisible,
            lowScreen,
            overlapToolbar: this.options.doOverlapToolbar,
            overlapCaption: this.options.doOverlapImageWithCaption,
            imagePadding: this.options.imagePadding,
            topBarHeight,
            captionHeight,
            safeArea: safeAreaOf(root),
        });
    }

    _registerUIElements() {
        this._gallery.on('uiRegister', () => {

            this._gallery.ui.registerElement({
                name: 'custom-caption',
                order: 9,
                isButton: false,
                appendTo: 'root',
                html: 'Caption text',
                onInit: (el, pswp) => {
                    pswp.on('change', () => {
                        const currSlideElement = this.getCurrentSlide();
                        let captionHTML = '';
                        if (currSlideElement) {
                            captionHTML = currSlideElement.title;
                        }

                        el.innerHTML = captionHTML || '';
                        if (captionHTML.length > 0 ) {
                            el.style.display = 'block';
                        } else {
                            el.style.display = 'none';
                        }

                    });
                }
            });

            this._gallery.ui.registerElement({
                name: 'share-button',
                ariaLabel: this.translations.share,
                title: this.translations.share,
                order: 10,
                isButton: true,
                html: icon('share', 'eg-icon--lg'),
                onInit: (el, pswp) => {
                     
                    pswp.on('change', (a,) => {
                        if (this.getCurrentSlide().shareUrl) {
                            el.style.display = 'block';
                        } else {
                            el.style.display = 'none';
                        }
                    });
                },
                 
                onClick: (event, el, pswp) => {
                    this.overlay = new Overlay();
                    let url = this.getCurrentSlide().shareUrl
                    this.overlay.openOverlay(url);
                    return false;
                }
            });

            this._gallery.ui.registerElement({
                name: 'add2cart-button',
                ariaLabel: this.translations.buy,
                title: this.translations.buy,
                order: 11,
                isButton: true,
                html: icon('cart-plus', 'eg-icon--lg'),
                onInit: (el, pswp) => {

                    pswp.on('change', (a,) => {
                        const slide = this.getCurrentSlide();
                        if (slide.add2cartUrl) {
                            el.style.display = 'block';
                        } else {
                            el.style.display = 'none';
                        }

                        // one verb for the action: "Buy image", "Buy video" on the slide of a video
                        const name = slide.type === 'video' && this.translations.buyVideo ? this.translations.buyVideo : this.translations.buy;
                        if (name) {
                            el.setAttribute('aria-label', name);
                            el.setAttribute('title', name);
                        }
                    });
                },

                onClick: (event, el, pswp) => {
                    this.overlay = new Overlay();

                    const overlay = this.overlay;
                    overlay.onLoad = () => {
                        // the dialog and its signal: the content script looks inside it and its listeners end with it
                        document.dispatchEvent(Polyfill.createNewEvent(Events.prepareAdd2cartDialogContent, {root: overlay.myDialogElement, signal: overlay.signal}));
                    };

                    let url = this.getCurrentSlide().add2cartUrl;
                    this.overlay.openOverlay(url);

                    return false;
                }

            });

            this._gallery.ui.registerElement({
                name: 'download-button',
                ariaLabel: this.translations.download,
                title: this.translations.download,
                order: 12,
                isButton: true,
                html: icon('download', 'eg-icon--lg'),
                onInit: (el, pswp) => {
                     
                    pswp.on('change', (a,) => {
                        if (this.getCurrentSlide().directDownloadButton) {
                            el.style.display = 'block';
                        } else {
                            el.style.display = 'none';
                        }

                    });
                },
                 
                onClick: (event, el) => {
                    this.getCurrentSlide().directDownloadButton?.click();
                    return false;
                }
            });

            new SlideShow(this._gallery);
        })


    }

    /**
     * searches the galleries for a thumbnail container and returns the
     * Slide object
     *
     * @param thumbEl
     * @returns PhotoSwipeGallerySlide
     */
    getSlideBy(thumbEl) {
        let galleries = this.getGalleries();

        for(let gallery of galleries.values()) {
            for (let i = 0, l = gallery.length; i < l; i++) {
                if (gallery[i].thumbEl === thumbEl || gallery[i].otherThumbEls.includes(thumbEl)) {
                    return gallery[i];
                }
            }
        }

        return null;
    }


    /**
     *
     * @param slide PhotoSwipeGallerySlide
     * @returns {*|number}
     */
    getIndexOfSlide(slide) {
        let slides = this.getGalleries().get(slide.gid);
        return slides.map(function(e) { return e.hash(); }).indexOf(slide.hash());
    }

    /**
     * Parses all elements on the page so we know which triggers for a lightbox exist.
     * Returns a mal containing the gallery and all elements
     * @returns {Map<String, Array>}
     */
    getGalleries() {
        let galleries = new Map(),
            thumbnails = getThumbnailContainers();

        for(let i=0; i<thumbnails.length; i++) {
            let thumbEl = thumbnails[i];
            let gallery;

            let item = parseThumbnailElement(thumbEl);
            if (item !== null) {
                if (galleries.get(item.gid) === undefined) {
                    galleries.set(item.gid, []);
                }
                gallery = galleries.get(item.gid);
                // An image which the gallery holds already is no second slide: every tag of one
                // event in an article joins the gallery of the event (so mode=hidden can add
                // images to it), and several tags may show the same image. Until 7.0.0 such an
                // image stood in the lightbox as often as on the page, and the Buy button of a
                // later tag with the cart was lost. The first thumbnail stays the slide and takes
                // over the cart, share and download controls of a later one which it lacks.
                const known = gallery.find((slide) => slide.pid === item.pid);
                if (known) {
                    PhotoSwipeGallery._mergeSlide(known, item);
                    continue;
                }
                gallery.push(item);
                galleries.set(item.gid, gallery);
            }
        }

        return galleries;
    }

    /**
     * Folds a later thumbnail of the same image into the slide: its controls where the slide has
     * none, and the thumbnail itself, so a click on it finds the slide. A thumbnail on the page
     * becomes the one the lightbox zooms from when the slide's own is hidden (mode=hidden).
     *
     * @param {PhotoSwipeGallerySlide} slide
     * @param {PhotoSwipeGallerySlide} duplicate
     */
    static _mergeSlide(slide, duplicate) {
        if (!slide.add2cartUrl && duplicate.add2cartUrl) {
            slide.add2cartUrl = duplicate.add2cartUrl;
        }
        if (!slide.shareUrl && duplicate.shareUrl) {
            slide.shareUrl = duplicate.shareUrl;
        }
        if (!slide.directDownloadButton && duplicate.directDownloadButton) {
            slide.directDownloadButton = duplicate.directDownloadButton;
        }
        if (slide.thumbEl.getClientRects().length === 0 && duplicate.thumbEl.getClientRects().length > 0) {
            slide.otherThumbEls.push(slide.thumbEl);
            slide.thumbEl = duplicate.thumbEl;
        } else {
            slide.otherThumbEls.push(duplicate.thumbEl);
        }
    }

    /**
     * grabs the current slide from the lightbox
     * @returns PhotoSwipeGallerySlide
     */
    getCurrentSlide() {
        return this._gallery.currSlide.data;
    }

    /**
     * Returns the index if the current item in the lightbox
     *
     * @returns integer
     */
    getCurrentIndex() {
        return this._gallery.currIndex;
    }

    /**
     * Nothing to do since the review of #1894: the click of a thumbnail a script adds later
     * reaches onDocumentClick() like every other. Kept for the scripts of a site which call it
     * after they added thumbnails, as 6.x asked them to.
     */
    reload() {
    }

    /**
     * reports back if the lightbox is currently open
     * @returns {boolean}
     */
    isOpen() {
        return this._isOpen;
    }

    /**
     *
     * @param afterChangeFunction
     */
    setAfterChangeEventListener(afterChangeFunction) {
        // the lightbox waits for its library: the listener goes to the lightbox which opens then
        if (this._waiting) {
            this._changeListeners.push(afterChangeFunction);
            return;
        }
        this._gallery.on('change', afterChangeFunction);
    }

    /**
     * jump to a specific slide.
     * @param index
     */
    gotoSlide(index) {
        this._gallery.goTo(index);
    }

}

